// frontend/app/components/admin/LessonLibrarySection.js
//
// The admin lesson library: search everything that has been seeded, open one
// lesson, fix its wording, save.
//
// Its own tab, next to Lesson Seeding. That tab is about files on disk and
// getting them into a database; this one is about the rows once they are
// there. They meet at the lock — a lesson saved here is one seeding will now
// skip — which is what the blurb below is for.
//
// Search and filters are server-side, like the Users tab: a typed word is
// debounced and refetched from offset 0, never filtered client-side, because
// the client only ever holds one page.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";

import { MaterialCommunityIcons } from "@expo/vector-icons";

import { useAuth } from "../../AuthContext";
import { PAGE_SIZE } from "./adminTable";
import { ADMIN_LESSON_COLORS as colors } from "./adminLessonColors";
import { listAdminLessonPlans, readAdminLessonError } from "../../lessonAdmin";
import { LESSON_BOARDS } from "./lessonAdminDraft";
import useAdminCurriculum from "./useAdminCurriculum";
import LessonLibraryEditor from "./LessonLibraryEditor";
import LessonLibraryFilters from "./LessonLibraryFilters";
import LessonLibraryList from "./LessonLibraryList";
import LessonWizard from "../lessonPlans/authoring/LessonWizard";
import LessonUploadButton from "../lessonPlans/LessonUploadButton";

const SEARCH_DEBOUNCE_MS = 500;

// The wizard needs an anchor board even when the library's own filter is on
// "All boards" — the first board the filter chips offer is as good a default
// as any, and it is what the X-Content-DB header falls back to server-side.
const DEFAULT_WIZARD_BOARD = LESSON_BOARDS[0];

const LessonLibrarySection = () => {
  const { user, fetchWithAuth } = useAuth() || {};

  const [queryText, setQueryText] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [board, setBoard] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openLessonId, setOpenLessonId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Curriculum for the wizard's topic picker, anchored to the library's own
  // board filter (or the default board, when "All boards" is selected). Only
  // fetched while the wizard is actually open — a null board here is what
  // makes the hook a no-op the rest of the time.
  const wizardBoard = creating ? board || DEFAULT_WIZARD_BOARD : null;
  const {
    curriculum: wizardCurriculum,
    loading: wizardCurriculumLoading,
    error: wizardCurriculumError,
  } = useAdminCurriculum(wizardBoard);

  // Filters can be changed faster than the server answers; only the newest
  // request is allowed to write the list.
  const requestRef = useRef(0);
  const searchTimeoutRef = useRef(null);

  const load = useCallback(async () => {
    const ticket = requestRef.current + 1;
    requestRef.current = ticket;
    setLoading(true);
    setError(null);

    try {
      const page = await listAdminLessonPlans({
        q: appliedQuery,
        board,
        status,
        limit: PAGE_SIZE,
        offset,
      });
      if (requestRef.current !== ticket) return;
      setItems(page.items);
      setTotal(page.total);
    } catch (err) {
      if (requestRef.current !== ticket) return;
      setItems([]);
      setTotal(0);
      setError(readAdminLessonError(err, "Could not load the lesson library."));
    } finally {
      if (requestRef.current === ticket) setLoading(false);
    }
  }, [appliedQuery, board, status, offset]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const clearPendingSearch = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  };

  // The panel's own debounce leaves its timer running past unmount, which is
  // how it produces a state update on a screen nobody is looking at.
  useEffect(() => clearPendingSearch, []);

  // A new query or filter makes the current page number meaningless — page 3
  // of the old result set is not page 3 of the new one.
  const onQueryChange = (text) => {
    setQueryText(text);
    clearPendingSearch();
    searchTimeoutRef.current = setTimeout(() => {
      setOffset(0);
      setAppliedQuery(text.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  const onSearchPress = () => {
    clearPendingSearch();
    setOffset(0);
    setAppliedQuery(queryText.trim());
  };

  const applyBoard = (next) => {
    setOffset(0);
    setBoard(next);
  };
  const applyStatus = (next) => {
    setOffset(0);
    setStatus(next);
  };

  const page = (direction) =>
    setOffset((current) => Math.max(0, current + direction * PAGE_SIZE));

  if (openLessonId) {
    return (
      <ScrollView contentContainerStyle={styles.body}>
        <LessonLibraryEditor
          colors={colors}
          lessonPlanId={openLessonId}
          onClose={() => setOpenLessonId(null)}
          onSaved={() => setRefreshKey((n) => n + 1)}
        />
      </ScrollView>
    );
  }

  // The wizard reports a saved document (from Finish) or null (from its own
  // close button, i.e. a cancel) through this one callback. A saved document
  // carries the new lesson's id, which is what turns this into "open it in
  // the plain editor" rather than "go back to an unchanged list".
  if (creating) {
    return (
      <ScrollView contentContainerStyle={styles.body}>
        {wizardCurriculumLoading ? (
          <Text style={styles.curriculumStatus}>Loading curriculum…</Text>
        ) : null}
        {wizardCurriculumError ? (
          <Text style={styles.curriculumError}>{wizardCurriculumError}</Text>
        ) : null}
        <LessonWizard
          isDarkMode={false}
          board={wizardBoard}
          curriculum={wizardCurriculum}
          ownerEmail={user?.email}
          fetchWithAuth={fetchWithAuth}
          editLessonPlanId={null}
          onClose={(saved) => {
            setCreating(false);
            const id = saved?.lesson_plan_id ?? saved?.id ?? null;
            if (id) {
              setRefreshKey((n) => n + 1);
              setOpenLessonId(id);
            }
          }}
        />
      </ScrollView>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.body}>
      <View style={styles.headerRow}>
        <Text style={styles.heading}>Lesson Library</Text>
        <View style={{ flex: 1 }} />
        <TouchableOpacity
          onPress={() => setCreating(true)}
          accessibilityRole="button"
          accessibilityLabel="Create lesson"
          style={styles.createBtn}
        >
          <MaterialCommunityIcons name="pencil-outline" size={16} color="#fff" />
          <Text style={styles.createBtnText}>Create lesson</Text>
        </TouchableOpacity>
        <LessonUploadButton
          fetchWithAuth={fetchWithAuth}
          style={styles.uploadWrap}
          onImported={(lesson) => {
            const id = lesson?.lesson_plan_id ?? lesson?.id ?? null;
            setRefreshKey((n) => n + 1);
            if (id) setOpenLessonId(id);
          }}
        />
        <TouchableOpacity
          onPress={() => setRefreshKey((n) => n + 1)}
          disabled={loading}
          accessibilityRole="button"
          style={[styles.refresh, loading && styles.refreshDisabled]}
        >
          <Text style={styles.refreshText}>Refresh</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.blurb}>
        Every lesson in this environment's database, whether it came from a file or was
        edited here. Editing a lesson locks it: an ordinary seed then skips it and reports
        it as skipped, so a fix made here survives the next deploy. Unlocking gives the
        file control back, and the next seed discards the edit. A forced seed on the
        Lesson Seeding tab overwrites a locked lesson and clears the lock.
      </Text>

      <LessonLibraryFilters
        colors={colors}
        queryText={queryText}
        board={board}
        status={status}
        onQueryTextChange={onQueryChange}
        onSubmit={onSearchPress}
        onBoardChange={applyBoard}
        onStatusChange={applyStatus}
      />

      <LessonLibraryList
        colors={colors}
        items={items}
        total={total}
        offset={offset}
        loading={loading}
        error={error}
        fetchWithAuth={fetchWithAuth}
        onOpen={(item) => setOpenLessonId(item.id)}
        onPage={page}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  body: { paddingBottom: 24 },
  headerRow: { flexDirection: "row", alignItems: "center", marginBottom: 8 },
  curriculumStatus: { fontSize: 12, color: colors.textMuted, padding: 12 },
  curriculumError: { fontSize: 12, color: colors.danger, padding: 12 },
  heading: { fontSize: 20, fontWeight: "700", color: "#25313d" },
  refresh: {
    borderWidth: 1,
    borderColor: "#c6d3df",
    backgroundColor: "#fff",
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  refreshDisabled: { opacity: 0.5 },
  refreshText: { color: "#355065", fontWeight: "700", fontSize: 12 },
  createBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: colors.accentPrimary,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    marginRight: 8,
  },
  uploadWrap: { marginRight: 8 },
  createBtnText: { color: "#fff", fontWeight: "700", fontSize: 12 },
  blurb: { fontSize: 12, lineHeight: 18, color: "#5a6772", marginBottom: 12 },
});

export default LessonLibrarySection;
